import { formatTokenDate } from '@/lib/mcp-access';
import type { McpToken } from '@mss_creator/shared';
import { Badge, Button, Table } from '@pms-digital/design-system';

interface McpTokenTableProps {
  tokens: McpToken[];
  /** Opens the revocation confirmation for one active token. */
  onRevoke: (token: McpToken) => void;
}

/**
 * The caller's tokens: name, creation, last use, status, and a « Révoquer »
 * action on the active ones. A revoked token stays listed, with its date, so
 * the history of what was handed out is not lost.
 */
export function McpTokenTable({ tokens, onRevoke }: McpTokenTableProps) {
  return (
    <Table
      caption="Mes jetons MCP"
      rows={tokens}
      rowKey={(token) => token.id}
      columns={[
        { key: 'name', header: 'Nom', cell: (token) => token.name },
        { key: 'created', header: 'Créé le', cell: (token) => formatTokenDate(token.createdAt) },
        {
          key: 'lastUsed',
          header: 'Dernier usage',
          cell: (token) =>
            token.lastUsedAt === null ? 'Jamais utilisé' : formatTokenDate(token.lastUsedAt),
        },
        {
          key: 'status',
          header: 'Statut',
          cell: (token) =>
            token.status === 'active' ? (
              <Badge tone="success" icon="check_circle" maxWidth="none">
                Actif
              </Badge>
            ) : (
              <span className="inline-flex flex-wrap items-center gap-2">
                <Badge tone="neutral" icon="block" maxWidth="none">
                  Révoqué
                </Badge>
                {token.revokedAt !== null && (
                  <span className="text-caption text-gray-600">
                    {`le ${formatTokenDate(token.revokedAt)}`}
                  </span>
                )}
              </span>
            ),
        },
        {
          key: 'actions',
          header: <span className="sr-only">Actions</span>,
          align: 'end',
          cell: (token) =>
            token.status === 'active' ? (
              <Button
                variant="destructive"
                size="sm"
                aria-label={`Révoquer « ${token.name} »`}
                onClick={() => onRevoke(token)}
              >
                Révoquer
              </Button>
            ) : null,
        },
      ]}
    />
  );
}
